/**
 * Modal-Host — zentraler Renderer fuer alle App-Modals (Versand/Storno/Refund/
 * Vario-Artikel/Bulk/GPSR/Source-Picker). Markup folgt der Design-Reference
 * (kl-modal-wrap > kl-modal-backdrop + kl-modal[head/body/foot]); die CSS-
 * Klassen leben in packages/frontend-design/styles/app.css.
 *
 *   const modal = openModal({
 *     title: 'Versand bestaetigen',
 *     icon: 'fa-truck-fast',
 *     body: '<div>…</div>',
 *     footer: '<button class="v-button" data-modal-cancel>Abbrechen</button>',
 *     onMount: (root) => { ... },
 *     onClose: () => { ... },
 *   });
 *   modal.close();
 *
 * Schliessen via: ESC, Klick auf Backdrop, Klick auf .kl-modal-close, oder
 * jedes [data-modal-cancel]-Element innerhalb des Modals. Body-Scroll wird
 * gelockt solange mindestens ein Modal offen ist.
 */
/**
 * Baut die beiden Inline-Style-Werte der Modal-Huelle: `card` fuer die `.kl-modal`-Karte,
 * `wrap` fuer den `.kl-modal-wrap`-Container.
 *
 * Bewusst als reine Funktion ausgelagert und exportiert. Sie ist die einzige Stelle mit
 * Fallunterscheidung, und so laesst sie sich ohne DOM testen. Wichtigster Fall: ohne die neuen
 * Optionen kommt exakt dasselbe heraus wie vorher (`wrap` leer, `card` nur Breite und
 * `max-width`), damit kein bestehendes Modal sein Aussehen aendert.
 */
export function buildModalStyles(opts) {
    let card = `width:${opts.width}px;max-width:calc(100vw - 32px);`;
    if (opts.maxHeight !== undefined) {
        card += `max-height:min(calc(100vh - 48px), ${opts.maxHeight}px);`;
    }
    return { card, wrap: opts.align === 'top' ? 'align-items:flex-start;' : '' };
}
let openCount = 0;
let prevBodyOverflow = '';
export function openModal(opts) {
    const root = document.createElement('div');
    root.className = 'kl-modal-wrap';
    const width = opts.width ?? 540;
    const dismissible = opts.dismissible !== false;
    const styles = buildModalStyles({ width, align: opts.align, maxHeight: opts.maxHeight });
    // Nur setzen, wenn wirklich etwas drinsteht: ohne die neuen Optionen bekommt der Wrapper
    // gar kein style-Attribut, das DOM bestehender Modals bleibt also Zeichen fuer Zeichen gleich.
    if (styles.wrap)
        root.setAttribute('style', styles.wrap);
    root.innerHTML = `
    <div class="kl-modal-backdrop" data-modal-backdrop></div>
    <div class="kl-modal" role="dialog" aria-modal="true" style="${styles.card}">
      <div class="kl-modal-head">
        <div class="kl-modal-title">
          ${opts.icon ? `<span class="v-icon fa-light ${opts.icon}"></span>` : ''}
          <span>${opts.title}</span>
        </div>
        <button type="button" class="kl-action-icon kl-modal-close" aria-label="Schließen">
          <span class="v-icon fa-light fa-xmark"></span>
        </button>
      </div>
      <div class="kl-modal-body">${opts.body ?? ''}</div>
      <div class="kl-modal-foot">${opts.footer ?? ''}</div>
    </div>
  `;
    document.body.appendChild(root);
    // Naechster Frame: is-open fuer CSS-Transition.
    requestAnimationFrame(() => root.classList.add('is-open'));
    if (openCount === 0) {
        prevBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
    }
    openCount++;
    let closed = false;
    const close = () => {
        if (closed)
            return;
        closed = true;
        document.removeEventListener('keydown', onKey);
        root.remove();
        openCount = Math.max(0, openCount - 1);
        if (openCount === 0)
            document.body.style.overflow = prevBodyOverflow;
        opts.onClose?.();
    };
    const onKey = (e) => {
        if (!dismissible)
            return;
        if (e.key === 'Escape') {
            e.stopPropagation();
            close();
        }
    };
    document.addEventListener('keydown', onKey);
    if (dismissible) {
        const graceMs = opts.dismissGraceMs ?? 0;
        const openedAt = performance.now();
        root.querySelector('[data-modal-backdrop]')?.addEventListener('click', () => {
            if (graceMs > 0 && performance.now() - openedAt < graceMs)
                return; // zweiter Klick eines Doppelklicks
            close();
        });
    }
    root.querySelector('.kl-modal-close')?.addEventListener('click', close);
    // Beliebige [data-modal-cancel]-Buttons im Body/Footer schliessen ebenfalls.
    root.addEventListener('click', (e) => {
        const target = e.target;
        if (target?.closest('[data-modal-cancel]'))
            close();
    });
    const el = root.querySelector('.kl-modal');
    opts.onMount?.(root);
    return { root, el, close };
}
//# sourceMappingURL=modal.js.map